"use client";

import { Button, HStack, Text, VStack } from "@chakra-ui/react";
import type {
  DegreeOfAbstraction,
  DegreeOfImpliciteness,
  DegreeOfIntention,
  MeiliOrigin,
  MindstringPrototype,
  SentenceStructure,
  Situation,
  SupportedLanguage,
} from "@/generated/fastapi";
import GenericCollapsible from "@/shared/ui/display/GenericCollapsible";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericInteractiveGuidance from "@/shared/ui/display/GenericInteractiveGuidance";
import BrandedPrototypeSelector from "@/shared/ui/controls/BrandedPrototypeSelector";
import ContentItemDetailModeHeader from "@/shared/ui/display/ContentItemDetailModeHeader";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import GenericSlider from "@/shared/ui/controls/GenericSlider";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_LOADING_STATES,
  PROFILE_SHAPING_MESSAGES,
} from "@/features/profile-shaping";
import { SuggestedThoughtsReviewSurface } from "@/features/thoughts";
import useSourceExtractionController from "./useSourceExtractionController";

type SourceExtractionWorkflowProps = {
  allowUnanchoredExtraction?: boolean;
  meiliOrigin: MeiliOrigin;
  onDeleteSearchResultAction?: (originId: string) => void;
  onUpdateSearchResultAction?: (updatedOrigin: MeiliOrigin) => void;
  presetSituation?: Situation | null | undefined;
  presetLanguage?: SupportedLanguage | null | undefined;
  presetPrototype?: MindstringPrototype | null | undefined;
  skipFetchSuggestedThoughts?: boolean;
  showOrientation?: boolean;
  onCancel?: () => void;
  showReturnHeader?: boolean;
};

type SourceExtractionController = ReturnType<
  typeof useSourceExtractionController
>;

type SourceExtractionInfoProps = {
  drawerInfoItems: SourceExtractionController["drawerInfoItems"];
  originPreview: string;
};

function SourceExtractionInfo({
  drawerInfoItems,
  originPreview,
}: SourceExtractionInfoProps) {
  return (
    <GenericDrawerInfo
      description={PROFILE_SHAPING_MESSAGES.shapeFromSource}
      items={[
        {
          label: PROFILE_SHAPING_LABELS.extractionPhase,
          colorPalette: "blue",
        },
        ...drawerInfoItems,
      ]}
    >
      <Text
        fontSize="xs"
        color="moveon.text.muted"
        lineHeight="tall"
        whiteSpace="pre-wrap"
        overflowWrap="anywhere"
        lineClamp={{ base: 4, md: 3 }}
      >
        {originPreview}
      </Text>
    </GenericDrawerInfo>
  );
}

type ExtractionControlsProps = {
  extraction: SourceExtractionController["extraction"];
  ui: SourceExtractionController["ui"];
};

function ExtractionControls({
  extraction,
  ui,
}: ExtractionControlsProps) {
  return (
    <VStack align="stretch" gap="3">
      <VStack align="stretch" gap="1">
        <Text fontWeight="semibold" color="moveon.surface.fg">
          {PROFILE_SHAPING_LABELS.backlogSourceCta}
        </Text>
        <Text fontSize="sm" color="moveon.text.muted" lineHeight="tall">
          Choose the profile perspective and situation before shaping
          suggestions from this source.
        </Text>
      </VStack>

      <BrandedPrototypeSelector
        activePrototype={extraction.selectedPrototype}
        onChange={extraction.setSelectedPrototype}
        options={ui.prototypeItems}
        disabled={extraction.selectedPrototypeLocked}
        ariaLabel="Choose perspective"
        showCounts={true}
        showDescriptions={true}
        variant="compact"
      />

      <VStack gap="2" align="stretch" width="full">
        <VStack align="stretch" gap="2" width="full">
          <Text fontSize="sm" fontWeight="medium" color="moveon.surface.fg">
            {extraction.allowUnanchoredExtraction &&
            !extraction.targetSituationLocked
              ? "Anchor to situation (optional)"
              : "Anchor to situation"}
          </Text>
          <GenericSelector<Situation>
            key={extraction.situationSelectorKey}
            previewItems={extraction.situationItems}
            stateObject={extraction.targetSituation}
            stateObjectSetter={extraction.setTargetSituation}
            disabled={extraction.targetSituationLocked}
            // mode="combobox"
            mode="select"
            sourceItems={extraction.situations}
            placeholder="Anchor to situation"
            width="full"
            isLoading={extraction.isLoadingSituations}
          />

          <HStack gap="2" align="center" justify="flex-end" width="full">
            <Button
              type="button"
              variant="outline"
              onClick={extraction.handleToggleExtractionOptions}
              aria-label="Toggle extraction options"
              minW={{ base: "10", sm: "auto" }}
            >
              <GenericIcon
                icon="adjust"
                highlighted={extraction.hasCustomOptions}
              />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Options
              </Text>
            </Button>

            <Button
              type="button"
              bg="moveon.primary"
              onClick={extraction.handleExtract}
              loading={extraction.isExtracting}
              loadingText={PROFILE_SHAPING_LOADING_STATES.extraction.actionLabel}
              disabled={!extraction.canExtract || extraction.isExtracting}
              aria-label={PROFILE_SHAPING_LABELS.extractionPhase}
              minW={{ base: "10", sm: "auto" }}
              color="white"
            >
              <GenericIcon icon="extract" />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                {PROFILE_SHAPING_LABELS.extractionPhase}
              </Text>
            </Button>
          </HStack>
        </VStack>

        {extraction.disabledReason ? (
          <Text fontSize="xs" color="moveon.text.muted" lineHeight="tall">
            {extraction.disabledReason}
          </Text>
        ) : null}
        {extraction.unanchoredExtractionWarning ? (
          <GenericInteractiveGuidance
            title="Suggestions will need anchoring"
            message={extraction.unanchoredExtractionWarning}
            compactMessage="Choose a situation now, or anchor suggestions before confirming them."
            icon="warning"
            accentColor="moveon.warning"
            accentBg="moveon.warningLight"
            accentBorderColor="moveon.warningBorder"
          />
        ) : null}
      </VStack>
    </VStack>
  );
}

type ExtractionOptionsProps = {
  extraction: SourceExtractionController["extraction"];
  options: SourceExtractionController["options"];
  ui: SourceExtractionController["ui"];
};

function ExtractionOptions({
  extraction,
  options,
  ui,
}: ExtractionOptionsProps) {
  if (!extraction.isOptionsOpen) return null;

  return (
    <GenericCollapsible
      isOpen={extraction.isOptionsOpen}
      setIsOpenAction={extraction.setOptionsOpen}
      setChildrenBorder={true}
    >
      <VStack align="stretch" gap="3">
        <GenericSelector<SupportedLanguage>
          previewItems={options.languageItems}
          stateObject={options.targetLanguage}
          stateObjectSetter={options.setTargetLanguage}
          initValue={options.targetLanguageInitValue}
          labelText="Return in language"
          mode="select"
          placeholder="Choose or confirm"
          disableClear={true}
          width="full"
        />
        <GenericSelector<SentenceStructure>
          previewItems={options.sentenceStructureItems}
          stateObject={options.sentenceStructure}
          stateObjectSetter={options.setSentenceStructure}
          labelText="Enforce structure"
          placeholder="Choose or confirm"
          mode="select"
          disableClear={true}
          showDescription={true}
          width="full"
        />
        <GenericSlider<DegreeOfImpliciteness>
          title="Degree of implicitness"
          activeState={[options.degreeOfImpliciteness]}
          setActiveState={(values) => {
            options.setDegreeOfImpliciteness(values[0]);
          }}
          options={options.degreeOfImplicitenessItems}
          size={ui.isMobileDevice ? "sm" : "md"}
        />
        <GenericSlider<DegreeOfAbstraction>
          title="Degree of abstraction"
          activeState={[options.degreeOfAbstraction]}
          setActiveState={(values) => {
            options.setDegreeOfAbstraction(values[0]);
          }}
          options={options.degreeOfAbstractionItems}
          size={ui.isMobileDevice ? "sm" : "md"}
        />
        <GenericSlider<DegreeOfIntention>
          title="Degree of intention"
          activeState={[options.degreeOfIntention]}
          setActiveState={(values) => {
            options.setDegreeOfIntention(values[0]);
          }}
          options={options.degreeOfIntentionItems}
          size={ui.isMobileDevice ? "sm" : "md"}
        />
      </VStack>
    </GenericCollapsible>
  );
}

export default function SourceExtractionWorkflow({
  allowUnanchoredExtraction = true,
  meiliOrigin,
  onUpdateSearchResultAction,
  presetSituation,
  presetLanguage,
  presetPrototype,
  skipFetchSuggestedThoughts = false,
  showOrientation = true,
  onCancel,
  showReturnHeader = Boolean(onCancel),
}: SourceExtractionWorkflowProps) {
  const controller = useSourceExtractionController({
    allowUnanchoredExtraction,
    meiliOrigin,
    onUpdateSearchResultAction,
    presetSituation,
    presetLanguage,
    presetPrototype,
    skipFetchSuggestedThoughts,
  });

  return (
    <VStack spaceY="4" align="stretch">
      {showOrientation ? (
        <SourceExtractionInfo
          drawerInfoItems={controller.drawerInfoItems}
          originPreview={controller.originPreview}
        />
      ) : null}
      {onCancel && showReturnHeader ? (
        <ContentItemDetailModeHeader
          title={PROFILE_SHAPING_LABELS.backlogSourceCta}
          onReturn={onCancel}
          returnAriaLabel="Back to source material"
        />
      ) : null}
      <ExtractionControls
        extraction={controller.extraction}
        ui={controller.ui}
      />
      <ExtractionOptions
        extraction={controller.extraction}
        options={controller.options}
        ui={controller.ui}
      />
      <SuggestedThoughtsReviewSurface
        deferSearchUntilResults
        enabled={!skipFetchSuggestedThoughts}
        hasRunExtraction={controller.extraction.hasRunExtraction}
        originId={meiliOrigin.objectID}
        orientationDescription={PROFILE_SHAPING_MESSAGES.reviewSuggestions}
        prototype={controller.extraction.selectedPrototype}
        showOrientation={false}
        showPrototypeSelector={false}
        showSituationSelector={false}
        situation={controller.extraction.targetSituation[0] ?? null}
        scopeItems={controller.review.scopeItems}
        isExtracting={controller.extraction.isExtracting}
      />
    </VStack>
  );
}
